import type { HelpContent, HelpSection } from '../portfolio_definition/HelpPanel';

/**
 * THE MODULE-WIDE RULE, said once in the drawer.
 *
 * Owner ruling, 2026-09-29: *"only projects that are finalized are eligible for selection,
 * monitoring and Project management. The exceptions are Project Creation screen and Project
 * Timeline Finalization screen."* The server enforces it in `lib/finalizedProjects.ts`; this
 * is the same rule written for the person reading the screen, so an empty picker reads as a
 * rule rather than as a fault.
 *
 * NOT IN THE SOURCE — the source has one project table and no such rule. Added here, and
 * added by WRAPPING each screen's help object at render time rather than by editing twenty
 * cloned objects: the source copy stays byte-for-byte what it was, and the note cannot drift
 * from one screen to the next because there is only one of it.
 */
export const FINALIZED_RULE: HelpSection = {
  label: 'Which projects you can pick',
  type: 'html',
  html:
    '<strong>Only FINALIZED projects are offered here.</strong> A project becomes selectable ' +
    'across Project Management — for selection, for monitoring and for management — once its ' +
    'timeline has been locked on <strong>Project Timeline Finalization</strong>. Until then its ' +
    'dates can still move, so anything recorded against it (a due date, a budget line, a sprint, ' +
    'a deck) would be a commitment against a schedule that does not exist yet.<br><br>' +
    'If the project picker on this screen is empty, that is the rule and not a fault: the ' +
    'project has not been finalized, or has not been pushed into Project Management at all. ' +
    '<strong>Project Creation</strong> and <strong>Project Timeline Finalization</strong> are ' +
    'the only two screens that see a project before it is finalized — which is what makes it ' +
    'possible to create one and finalize it.',
};

/** The same rule, written for one of the two screens that IS the exception. */
export const FINALIZED_RULE_EXCEPTION: HelpSection = {
  label: 'Which projects you can pick',
  type: 'html',
  html:
    '<strong>This is one of the two screens that sees a project before it is finalized</strong> ' +
    '— the other is <strong>Project Creation</strong>. Everywhere else in Project Management ' +
    'offers <strong>finalized projects only</strong>, for selection, for monitoring and for ' +
    'management.<br><br>Finalizing a timeline here is therefore what releases a project to the ' +
    'rest of the module: Work Item Definition, RACI, Budget and Resource planning, the ' +
    'Execution Control Center, the Action Register, status decks and closure all begin offering ' +
    'it the moment it is finalized.',
};

/** Append the rule to a screen's help without touching its cloned copy. */
export const withFinalizedRule = (h: HelpContent, rule: HelpSection = FINALIZED_RULE): HelpContent => ({
  ...h,
  sections: [...(h.sections ?? []), rule],
});
